/**
 * AudioRangeSelector — Integrated audio player with waveform and
 * **multi-range** selection. The user can add up to `maxRanges` regions on
 * the waveform; each range is independently resized, previewed, and
 * deleted. Final ranges are reported up via `onRangesChange`.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { Region } from 'wavesurfer.js/dist/plugins/regions.js';
import ZoomPlugin from 'wavesurfer.js/dist/plugins/zoom.js';
import { getFullAudioUrl } from '../../utils/audioUrl';
import './AudioRangeSelector.css';

const PREVIEW_DURATION = 3;
const DEFAULT_ZOOM = 100;
const ZOOM_STEP = 50;
const MIN_ZOOM = 5;
const MAX_ZOOM = 450;
const MIN_RANGE_DURATION = 1; // seconds — guard against accidental zero-length ranges
const REGION_COLORS = [
  { fill: 'rgba(37, 99, 235, 0.22)', handle: '#2563EB' },   // blue
  { fill: 'rgba(124, 58, 237, 0.22)', handle: '#7C3AED' },  // purple
  { fill: 'rgba(16, 185, 129, 0.22)', handle: '#10B981' },  // green
  { fill: 'rgba(234, 88, 12, 0.22)', handle: '#EA580C' },   // orange
  { fill: 'rgba(220, 38, 38, 0.22)', handle: '#DC2626' },   // red
];

export interface AudioRange {
  start: number;
  end: number;
}

interface RangeWithId extends AudioRange {
  id: string;
}

interface AudioRangeSelectorProps {
  audioUrl: string;
  duration: number;
  onRangesChange: (ranges: AudioRange[]) => void;
  onDurationLoaded?: (duration: number) => void;
  initialRanges?: AudioRange[];
  maxRanges?: number;
}

let nextRangeIdCounter = 0;
const makeRangeId = () => `range-${Date.now()}-${++nextRangeIdCounter}`;

const AudioRangeSelector: React.FC<AudioRangeSelectorProps> = React.memo(({
  audioUrl,
  duration: propDuration,
  onRangesChange,
  onDurationLoaded,
  initialRanges,
  maxRanges = 10,
}) => {
  const { t } = useTranslation(['creator', 'common']);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsPluginRef = useRef<RegionsPlugin | null>(null);
  const regionsByIdRef = useRef<Map<string, Region>>(new Map());
  const prevBoundsByIdRef = useRef<Map<string, { start: number; end: number }>>(new Map());
  const isDestroyedRef = useRef(false);
  const isUpdatingRegionRef = useRef(false);
  const isCleaningUpRef = useRef(false);

  // Captured-on-mount initial values
  const initialRangesRef = useRef(initialRanges);

  // Latest-callback refs
  const onDurationLoadedRef = useRef(onDurationLoaded);
  onDurationLoadedRef.current = onDurationLoaded;
  const onRangesChangeRef = useRef(onRangesChange);
  onRangesChangeRef.current = onRangesChange;

  // State
  const [duration, setDuration] = useState(propDuration);
  const [currentTime, setCurrentTime] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ranges, setRanges] = useState<RangeWithId[]>([]);
  const [activeRangeId, setActiveRangeId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<{ rangeId: string; side: 'start' | 'end' } | null>(null);
  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Volume state
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  // ─────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────

  const getColorForIndex = useCallback((index: number) => {
    return REGION_COLORS[index % REGION_COLORS.length];
  }, []);

  // Apply inline styles to a region handle (shadow DOM prevents CSS selectors).
  // Color is derived from the range's position-index so handles match the fill.
  const styleHandle = useCallback((handle: HTMLElement, side: 'left' | 'right', baseColor: string) => {
    if (handle.dataset.styled) return;
    handle.dataset.styled = 'true';

    const barWidth = isTouchDevice ? 4 : 3;
    const knobWidth = isTouchDevice ? 28 : 18;
    const knobHeight = isTouchDevice ? 22 : 16;

    Object.assign(handle.style, {
      backgroundColor: 'transparent',
      width: `${knobWidth}px`,
      cursor: 'col-resize',
      zIndex: '10',
      position: 'absolute',
      top: '0',
      height: '100%',
      overflow: 'visible',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    });
    if (side === 'left') {
      handle.style.left = `-${Math.floor(knobWidth / 2)}px`;
    } else {
      handle.style.right = `-${Math.floor(knobWidth / 2)}px`;
    }
    handle.style.borderLeft = 'none';
    handle.style.borderRight = 'none';

    const existingKnob = handle.querySelector('.handle-knob');
    if (!existingKnob) {
      const knob = document.createElement('div');
      knob.className = 'handle-knob';
      Object.assign(knob.style, {
        width: `${knobWidth}px`,
        height: `${knobHeight}px`,
        backgroundColor: baseColor,
        borderRadius: '4px',
        flexShrink: '0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'transform 0.15s ease',
        cursor: 'col-resize',
        backgroundImage: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent ${isTouchDevice ? '4px' : '3px'},
          rgba(255,255,255,0.4) ${isTouchDevice ? '4px' : '3px'},
          rgba(255,255,255,0.4) ${isTouchDevice ? '6px' : '5px'}
        )`,
        backgroundPosition: 'center',
        backgroundSize: `${Math.floor(knobWidth * 0.6)}px 100%`,
        backgroundRepeat: 'no-repeat',
      });
      handle.appendChild(knob);

      const bar = document.createElement('div');
      bar.className = 'handle-bar';
      Object.assign(bar.style, {
        width: `${barWidth}px`,
        flex: '1',
        backgroundColor: baseColor,
        borderRadius: '0 0 2px 2px',
        minHeight: '0',
      });
      handle.appendChild(bar);
    }

    handle.addEventListener('pointerenter', () => {
      const knob = handle.querySelector('.handle-knob') as HTMLElement | null;
      if (knob) knob.style.transform = 'scaleX(1.1)';
    });
    handle.addEventListener('pointerleave', () => {
      const knob = handle.querySelector('.handle-knob') as HTMLElement | null;
      if (knob) knob.style.transform = 'scaleX(1)';
    });
  }, [isTouchDevice]);

  // Re-style handles after a region's color changes (e.g. when ranges reorder).
  // We unset `data-styled` so styleHandle re-applies the new color.
  const restyleRegionHandles = useCallback((region: Region, baseColor: string) => {
    if (!region.element) return;
    const leftHandle = region.element.querySelector('[data-resize="left"]') as HTMLElement | null;
    const rightHandle = region.element.querySelector('[data-resize="right"]') as HTMLElement | null;
    [leftHandle, rightHandle].forEach((h, idx) => {
      if (!h) return;
      delete h.dataset.styled;
      // Clear any existing knob/bar children
      h.querySelectorAll('.handle-knob, .handle-bar').forEach(el => el.remove());
      styleHandle(h, idx === 0 ? 'left' : 'right', baseColor);
    });
  }, [styleHandle]);

  // Stop any in-flight preview playback.
  const stopPreview = useCallback(() => {
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    if (wavesurferRef.current?.isPlaying()) {
      wavesurferRef.current.pause();
    }
    setPreviewing(null);
  }, []);

  // Start preview playback for a specific range/side.
  const startPreview = useCallback((rangeId: string, side: 'start' | 'end') => {
    if (!wavesurferRef.current || !isReady) return;
    const range = regionsByIdRef.current.get(rangeId);
    if (!range) return;

    stopPreview();

    let previewStart: number;
    let previewEnd: number;
    if (side === 'start') {
      previewStart = range.start;
      previewEnd = Math.min(range.start + PREVIEW_DURATION, range.end);
    } else {
      previewStart = Math.max(range.end - PREVIEW_DURATION, range.start);
      previewEnd = range.end;
    }

    wavesurferRef.current.setTime(previewStart);
    wavesurferRef.current.play();
    setPreviewing({ rangeId, side });

    previewTimeoutRef.current = setTimeout(() => {
      if (!isDestroyedRef.current && wavesurferRef.current) {
        wavesurferRef.current.pause();
      }
      setPreviewing(null);
      previewTimeoutRef.current = null;
    }, (previewEnd - previewStart) * 1000);
  }, [isReady, stopPreview]);

  // ─────────────────────────────────────────
  // Region creation / wiring
  // ─────────────────────────────────────────

  const wireRegionEvents = useCallback((region: Region) => {
    // 'region-update' (drag-in-progress) — kill any preview so handles don't fight playback
    region.on('update', () => {
      if (isDestroyedRef.current) return;
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      if (wavesurferRef.current?.isPlaying()) {
        wavesurferRef.current.pause();
        setPreviewing(null);
      }
    });

    // 'region-updated' (drag released) — sync to state and play preview of the dragged side
    region.on('update-end', () => {
      if (isUpdatingRegionRef.current || isDestroyedRef.current) return;
      const wsDur = wavesurferRef.current?.getDuration() || 0;
      const newStart = Math.max(0, region.start);
      const newEnd = Math.min(wsDur, region.end);
      if (newEnd - newStart < MIN_RANGE_DURATION) {
        // Snap back if user shrunk too far
        const prev = prevBoundsByIdRef.current.get(region.id);
        if (prev) {
          isUpdatingRegionRef.current = true;
          region.setOptions({ start: prev.start, end: prev.end });
          isUpdatingRegionRef.current = false;
        }
        return;
      }

      const prev = prevBoundsByIdRef.current.get(region.id) ?? { start: newStart, end: newEnd };
      const startChanged = Math.abs(newStart - prev.start) > 0.01;
      const endChanged = Math.abs(newEnd - prev.end) > 0.01;
      prevBoundsByIdRef.current.set(region.id, { start: newStart, end: newEnd });

      setRanges(prevRanges => prevRanges.map(r =>
        r.id === region.id ? { ...r, start: newStart, end: newEnd } : r,
      ));
      setActiveRangeId(region.id);

      // Preview the side that moved
      stopPreview();
      if (wavesurferRef.current) {
        if (startChanged && !endChanged) {
          startPreview(region.id, 'start');
        } else if (endChanged && !startChanged) {
          startPreview(region.id, 'end');
        }
      }
    });

    // Click on region body — set active + seek to click position and play
    region.on('click', (e?: MouseEvent) => {
      if (isDestroyedRef.current || !wavesurferRef.current) return;
      e?.stopPropagation?.();
      setActiveRangeId(region.id);

      const regionElement = region.element;
      if (regionElement && e) {
        const rect = regionElement.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const regionWidth = rect.width;
        const regionDuration = region.end - region.start;
        const clickTime = region.start + (clickX / regionWidth) * regionDuration;
        wavesurferRef.current.setTime(clickTime);
        wavesurferRef.current.play();
      }
    });
  }, [startPreview, stopPreview]);

  // Create a region in WaveSurfer for a RangeWithId.
  const addWaveSurferRegion = useCallback((range: RangeWithId, colorIdx: number) => {
    if (!regionsPluginRef.current) return null;
    const color = getColorForIndex(colorIdx);
    const region = regionsPluginRef.current.addRegion({
      id: range.id,
      start: range.start,
      end: range.end,
      color: color.fill,
      drag: false,
      resize: true,
    });
    regionsByIdRef.current.set(range.id, region);
    prevBoundsByIdRef.current.set(range.id, { start: range.start, end: range.end });

    if (region.element) {
      const leftHandle = region.element.querySelector('[data-resize="left"]') as HTMLElement | null;
      const rightHandle = region.element.querySelector('[data-resize="right"]') as HTMLElement | null;
      if (leftHandle) styleHandle(leftHandle, 'left', color.handle);
      if (rightHandle) styleHandle(rightHandle, 'right', color.handle);
    }
    wireRegionEvents(region);
    return region;
  }, [getColorForIndex, styleHandle, wireRegionEvents]);

  // ─────────────────────────────────────────
  // Initialize WaveSurfer
  // ─────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || !audioUrl) return;

    const fullUrl = getFullAudioUrl(audioUrl);

    isDestroyedRef.current = false;

    // AbortError suppression for StrictMode
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (
        isCleaningUpRef.current &&
        (event.reason?.name === 'AbortError' ||
          event.reason?.message?.includes('signal is aborted'))
      ) {
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    containerRef.current.innerHTML = '';

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#94A3B8',
      progressColor: '#2563EB',
      cursorColor: '#F97316',
      cursorWidth: 2,
      height: 80,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      barRadius: 3,
    });

    const regionsPlugin = ws.registerPlugin(RegionsPlugin.create());
    ws.registerPlugin(ZoomPlugin.create({ scale: 0.5, maxZoom: MAX_ZOOM, deltaThreshold: 5 }));

    wavesurferRef.current = ws;
    regionsPluginRef.current = regionsPlugin;

    ws.load(fullUrl).catch((err) => {
      if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
        return;
      }
      console.error('[AudioRangeSelector] Load error:', err);
    });

    ws.on('ready', () => {
      if (isDestroyedRef.current) return;

      const audioDuration = ws.getDuration();
      setDuration(audioDuration);
      setIsReady(true);
      ws.zoom(DEFAULT_ZOOM);
      setZoomLevel(DEFAULT_ZOOM);
      onDurationLoadedRef.current?.(audioDuration);

      // Seed ranges from initialRanges or default to single full-duration range.
      const seed: RangeWithId[] =
        initialRangesRef.current && initialRangesRef.current.length > 0
          ? initialRangesRef.current.map(r => ({
              id: makeRangeId(),
              start: Math.max(0, r.start),
              end: Math.min(audioDuration, r.end),
            }))
          : [{ id: makeRangeId(), start: 0, end: audioDuration }];

      // Render regions
      seed.forEach((range, idx) => addWaveSurferRegion(range, idx));
      setRanges(seed);
      setActiveRangeId(seed[0]?.id ?? null);
    });

    // No auto-stop on region end — multi-region playback lets user listen across boundaries
    ws.on('audioprocess', () => {
      if (isDestroyedRef.current) return;
      setCurrentTime(ws.getCurrentTime());
    });

    ws.on('seeking', () => {
      if (isDestroyedRef.current) return;
      setCurrentTime(ws.getCurrentTime());
    });

    ws.on('play', () => { if (!isDestroyedRef.current) setIsPlaying(true); });
    ws.on('pause', () => { if (!isDestroyedRef.current) setIsPlaying(false); });
    ws.on('finish', () => { if (!isDestroyedRef.current) setIsPlaying(false); });

    ws.on('zoom', (newZoom: number) => {
      if (!isDestroyedRef.current) setZoomLevel(Math.round(newZoom));
    });

    ws.on('error', (err) => {
      if (isDestroyedRef.current) return;
      console.error('[AudioRangeSelector] Error:', err);
    });

    // Capture ref values into local consts so cleanup uses the same Map
    // instances we observed during init (avoids react-hooks/exhaustive-deps).
    const regionsMap = regionsByIdRef.current;
    const prevBoundsMap = prevBoundsByIdRef.current;

    return () => {
      isDestroyedRef.current = true;
      isCleaningUpRef.current = true;
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      wavesurferRef.current = null;
      regionsPluginRef.current = null;
      regionsMap.clear();
      prevBoundsMap.clear();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      try {
        ws.destroy();
      } catch {
        // Suppress destroy errors (StrictMode)
      }
    };
    // Only re-init when audioUrl changes — initial range/duration props are captured via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  // Update duration when prop changes
  useEffect(() => {
    if (propDuration > 0) setDuration(propDuration);
  }, [propDuration]);

  // Notify parent of range changes
  useEffect(() => {
    onRangesChangeRef.current(ranges.map(r => ({ start: r.start, end: r.end })));
  }, [ranges]);

  // ─────────────────────────────────────────
  // Range list management
  // ─────────────────────────────────────────

  const addRange = useCallback(() => {
    if (!isReady || ranges.length >= maxRanges) return;
    const wsDur = wavesurferRef.current?.getDuration() || duration;
    if (wsDur <= MIN_RANGE_DURATION) return;

    // Find a gap or place after the last range
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const last = sorted[sorted.length - 1];
    const startAt = last ? Math.min(last.end + 0.5, wsDur - MIN_RANGE_DURATION) : 0;
    const endAt = Math.min(startAt + Math.max(5, wsDur * 0.1), wsDur);

    const newRange: RangeWithId = {
      id: makeRangeId(),
      start: Math.max(0, startAt),
      end: endAt,
    };

    addWaveSurferRegion(newRange, ranges.length);
    setRanges(prev => [...prev, newRange]);
    setActiveRangeId(newRange.id);
  }, [isReady, ranges, maxRanges, duration, addWaveSurferRegion]);

  const removeRange = useCallback((rangeId: string) => {
    const region = regionsByIdRef.current.get(rangeId);
    if (region) {
      try { region.remove(); } catch { /* already gone */ }
    }
    regionsByIdRef.current.delete(rangeId);
    prevBoundsByIdRef.current.delete(rangeId);

    setRanges(prev => {
      const filtered = prev.filter(r => r.id !== rangeId);
      // Always keep at least one range — replace with full-duration if empty.
      if (filtered.length === 0) {
        const wsDur = wavesurferRef.current?.getDuration() || duration;
        const newRange: RangeWithId = { id: makeRangeId(), start: 0, end: wsDur };
        addWaveSurferRegion(newRange, 0);
        setActiveRangeId(newRange.id);
        return [newRange];
      }
      // Recolor remaining regions to keep colors monotonic with position.
      filtered.forEach((r, idx) => {
        const region = regionsByIdRef.current.get(r.id);
        if (region) {
          const c = getColorForIndex(idx);
          region.setOptions({ color: c.fill });
          restyleRegionHandles(region, c.handle);
        }
      });
      return filtered;
    });
    setActiveRangeId(prev => (prev === rangeId ? null : prev));
  }, [duration, addWaveSurferRegion, getColorForIndex, restyleRegionHandles]);

  const resetAllRanges = useCallback(() => {
    stopPreview();
    // Remove all current regions
    regionsByIdRef.current.forEach(region => {
      try { region.remove(); } catch { /* ignore */ }
    });
    regionsByIdRef.current.clear();
    prevBoundsByIdRef.current.clear();

    const wsDur = wavesurferRef.current?.getDuration() || duration;
    const fresh: RangeWithId = { id: makeRangeId(), start: 0, end: wsDur };
    addWaveSurferRegion(fresh, 0);
    setRanges([fresh]);
    setActiveRangeId(fresh.id);
  }, [duration, addWaveSurferRegion, stopPreview]);

  // ─────────────────────────────────────────
  // Playback / volume / zoom
  // ─────────────────────────────────────────

  const handleZoomIn = useCallback(() => {
    if (!wavesurferRef.current || isDestroyedRef.current) return;
    const newZoom = Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM);
    wavesurferRef.current.zoom(newZoom);
    setZoomLevel(newZoom);
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    if (!wavesurferRef.current || isDestroyedRef.current) return;
    const newZoom = Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM);
    wavesurferRef.current.zoom(newZoom);
    setZoomLevel(newZoom);
  }, [zoomLevel]);

  const togglePlay = () => {
    if (!wavesurferRef.current || !isReady) return;
    stopPreview();
    if (isPlaying) {
      wavesurferRef.current.pause();
    } else {
      wavesurferRef.current.play();
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (wavesurferRef.current) wavesurferRef.current.setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!wavesurferRef.current) return;
    if (isMuted) {
      wavesurferRef.current.setVolume(volume || 1);
      setIsMuted(false);
    } else {
      wavesurferRef.current.setVolume(0);
      setIsMuted(true);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const totalSelectedDuration = ranges.reduce((sum, r) => sum + Math.max(0, r.end - r.start), 0);
  const canAddRange = isReady && ranges.length < maxRanges;
  const canReset =
    isReady && (ranges.length > 1 || ranges[0]?.start !== 0 || ranges[0]?.end !== duration);

  // Sort ranges for display (don't mutate state — original order is preserved internally)
  const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
  const indexById = new Map(ranges.map((r, idx) => [r.id, idx]));

  return (
    <div className="audio-range-selector integrated with-waveform">
      {/* Main playback controls */}
      <div className="playback-controls">
        <button
          type="button"
          className={`btn-play-pause ${!isReady ? 'disabled' : ''}`}
          onClick={togglePlay}
          disabled={!isReady}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="time-display">
          <span className="current-time">{formatTime(currentTime)}</span>
          <span className="time-separator">/</span>
          <span className="total-time">{formatTime(duration)}</span>
        </div>

        <div className="volume-control">
          <button
            type="button"
            className="btn-mute"
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted || volume === 0 ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            )}
          </button>
          <input
            type="range"
            className="volume-slider"
            min="0"
            max="1"
            step="0.05"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            aria-label="Volume"
          />
        </div>
      </div>

      {/* Waveform */}
      <div
        className="waveform-container"
        style={{ position: 'relative', zIndex: 1, minHeight: '80px' }}
        role="application"
        aria-label="Audio waveform range selector"
      >
        {!isReady && (
          <div className="waveform-loading">
            <div className="loading-spinner" />
            <span>{t('creator:workflow.audioRangeSelector.loadingWaveform')}</span>
          </div>
        )}
        <div
          ref={containerRef}
          className="waveform"
          style={zoomLevel > DEFAULT_ZOOM ? { overflowX: 'auto' } : undefined}
        />

        <div className="ars-zoom-controls">
          <button
            type="button"
            className="ars-tool-btn"
            onClick={handleZoomOut}
            disabled={zoomLevel <= MIN_ZOOM}
            aria-label="Zoom out"
            title="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            className="ars-tool-btn"
            onClick={handleZoomIn}
            disabled={zoomLevel >= MAX_ZOOM}
            aria-label="Zoom in"
            title="Zoom in"
          >
            +
          </button>
        </div>

        <div className="time-markers">
          <span className="marker-start">0:00</span>
          <span className="marker-end">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Range list + actions */}
      <div className="range-list-section">
        <div className="range-list-header">
          <span className="range-list-title">
            {t('creator:workflow.audioRangeSelector.rangeListHeader', {
              count: ranges.length,
              duration: formatTime(totalSelectedDuration),
            })}
          </span>
          <div className="range-list-actions">
            <button
              type="button"
              className="btn-add-range"
              onClick={addRange}
              disabled={!canAddRange}
              title={
                ranges.length >= maxRanges
                  ? t('creator:workflow.audioRangeSelector.maxRangesReached', { max: maxRanges })
                  : t('creator:workflow.audioRangeSelector.addRange')
              }
            >
              + {t('creator:workflow.audioRangeSelector.addRange')}
            </button>
            <button
              type="button"
              className="btn-reset-range"
              onClick={resetAllRanges}
              disabled={!canReset}
            >
              {t('creator:workflow.audioRangeSelector.resetToFull')}
            </button>
          </div>
        </div>

        <ul className="range-list">
          {sortedRanges.map((r) => {
            const colorIdx = indexById.get(r.id) ?? 0;
            const color = getColorForIndex(colorIdx);
            const isActive = r.id === activeRangeId;
            const dur = Math.max(0, r.end - r.start);
            return (
              <li
                key={r.id}
                className={`range-list__item ${isActive ? 'range-list__item--active' : ''}`}
                onClick={() => setActiveRangeId(r.id)}
                style={{ borderLeftColor: color.handle }}
              >
                <span className="range-list__index" style={{ background: color.handle }}>
                  {colorIdx + 1}
                </span>
                <button
                  type="button"
                  className={`range-badge start clickable ${previewing?.rangeId === r.id && previewing?.side === 'start' ? 'previewing' : ''}`}
                  onClick={(e) => { e.stopPropagation(); startPreview(r.id, 'start'); }}
                  disabled={!isReady}
                  title={t('creator:workflow.audioRangeSelector.previewStart')}
                >
                  <span className="badge-label">{t('creator:workflow.audioRangeSelector.start')}</span>
                  <span className="badge-value">{formatTime(r.start)}</span>
                </button>
                <span className="range-list__separator">→</span>
                <button
                  type="button"
                  className={`range-badge end clickable ${previewing?.rangeId === r.id && previewing?.side === 'end' ? 'previewing' : ''}`}
                  onClick={(e) => { e.stopPropagation(); startPreview(r.id, 'end'); }}
                  disabled={!isReady}
                  title={t('creator:workflow.audioRangeSelector.previewEnd')}
                >
                  <span className="badge-label">{t('creator:workflow.audioRangeSelector.end')}</span>
                  <span className="badge-value">{formatTime(r.end)}</span>
                </button>
                <span className="range-list__duration">{formatTime(dur)}</span>
                <button
                  type="button"
                  className="range-list__remove"
                  onClick={(e) => { e.stopPropagation(); removeRange(r.id); }}
                  aria-label={t('creator:workflow.audioRangeSelector.removeRange')}
                  title={t('creator:workflow.audioRangeSelector.removeRange')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <p className="range-hint">
        {t('creator:workflow.audioRangeSelector.dragHint')}
      </p>
    </div>
  );
});

AudioRangeSelector.displayName = 'AudioRangeSelector';

export default AudioRangeSelector;
